import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '@/config/api';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Users,
  Star,
  CheckCircle2,
  PartyPopper,
  FileText,
  Plus,
  Mail,
  ClipboardList,
  Inbox,
  AlertTriangle,
  RefreshCw,
  Upload,
  X,
  ExternalLink,
  Loader2,
  CircleCheck,
  CircleX,
  CircleAlert,
  Eye,
  Download,
} from 'lucide-react';

import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Skeleton } from '@/Components/ui/skeleton';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Textarea } from '@/Components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/Components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/Components/ui/dialog';
import { cn } from '@/lib/utils';

import RecruiterNavbar from './RecruiterNavbar';
import CandidateCardTabbed from './CandidateCardTabbed';
import EmailComposer from './EmailComposer';
import QuestionnaireResponsesViewer from './QuestionnaireResponses';
import JobDescriptionModal from './JobDescriptionModal';
import { useRecruiterAuth } from '../../../CompanyAdmin/Context/RecruiterAuthContext';
import { getApiUrl } from '../../../CompanyAdmin/utils/api';
import ResumePreview, { downloadResume } from '../Applications/ResumePreview';

// ============================================================================
// Small helpers (no SQL/network — just data shaping)
// ============================================================================

const isIntegerString = (v) => v != null && /^\s*\d+\s*$/.test(String(v));
const isIsoDateOrDatetime = (v) => {
  if (!v || typeof v !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?([.]\d+)?)?([Z\+\-].*)?$/.test(
    v.trim(),
  );
};
const extractDatePart = (iso) => {
  if (!iso) return '';
  const [datePart] = String(iso).trim().split(/[T ]/);
  return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : '';
};

// Filter chip definitions for the left sidebar.
// Most filter `id`s match candidate.status values returned by the API
// (shortlisted / hired / joined). The 'rejected' filter is special:
// rejection is tracked via the `isRejected` boolean column, NOT the
// status string — so the filter logic below special-cases it.
const FILTERS = [
  { id: 'all',         label: 'All Candidates', icon: Users },
  { id: 'shortlisted', label: 'Shortlisted',    icon: Star },
  { id: 'hired',       label: 'Hired',          icon: CheckCircle2 },
  { id: 'joined',      label: 'Joined',         icon: PartyPopper },
  // New tab — surfaces candidates the company admin has rejected so the
  // recruiter sees the decision and can stop following up. Mirrors the
  // company-side reject flow which sets job_applications_optimized.is_rejected.
  { id: 'rejected',    label: 'Rejected',       icon: CircleX },
];

// ============================================================================
// Toast — replaces the legacy `.jd-popup`. Auto-dismisses in 5s, like before.
// ============================================================================

function Toast({ open, type, title, message, onClose, closable = true }) {
  if (!open) return null;
  const tones = {
    success: 'border-emerald-500/40 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
    error: 'border-destructive/40 bg-destructive/10 text-destructive',
    warning: 'border-amber-500/40 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
    info: 'border-border bg-card text-foreground',
  };
  return (
    <div className="fixed top-4 right-4 z-[100] w-full max-w-sm">
      <Card className={cn('border', tones[type] || tones.info)}>
        <CardContent className="flex items-start gap-3 p-4">
          <div className="flex-1">
            <p className="text-sm font-semibold">{title}</p>
            <p className="mt-1 whitespace-pre-line text-xs opacity-90">{message}</p>
          </div>
          {closable && (
            <button
              onClick={onClose}
              className="rounded p-1 hover:bg-foreground/10"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// File status row inside the upload dialog
// ============================================================================

function UploadFileRow({ status, name, progress, error }) {
  const map = {
    queued: { icon: Loader2, tone: 'text-muted-foreground', label: 'Queued', spin: true },
    uploading: { icon: Loader2, tone: 'text-blue-600', label: 'Uploading', spin: true },
    processing: { icon: Loader2, tone: 'text-violet-600', label: 'Processing', spin: true },
    success: { icon: CircleCheck, tone: 'text-emerald-600', label: 'Success' },
    failed: { icon: CircleX, tone: 'text-destructive', label: 'Failed' },
    duplicate: { icon: CircleAlert, tone: 'text-amber-600', label: 'Duplicate' },
  };
  const { icon: Icon, tone, label, spin } = map[status] || map.queued;
  return (
    <div className="rounded-md border border-border/40 bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className={cn('h-4 w-4 shrink-0', tone, spin && 'animate-spin')} />
          <span className="truncate text-sm" title={name}>
            {name}
          </span>
        </div>
        <Badge variant="outline" className={cn('border-0 text-[10px]', tone)}>
          {label}
        </Badge>
      </div>

      {(status === 'uploading' || status === 'processing') && (
        <div className="mt-2 flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="w-9 text-right text-[10px] tabular-nums text-muted-foreground">
            {progress}%
          </span>
        </div>
      )}

      {error && (
        <div className="mt-1 flex items-center gap-1 text-[11px] text-destructive">
          <CircleAlert className="h-3 w-3 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Page
// ============================================================================

export function JobDetails() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const initialFilter = location.state?.filter || 'all';
  const jobId = searchParams.get('jobId');
  const { recruiter } = useRecruiterAuth();

  // ----- toast --------------------------------------------------------------
  const [popup, setPopup] = useState({
    open: false,
    type: '',
    title: '',
    message: '',
    closable: true,
  });

  // ----- post-upload edit-profile flow -------------------------------------
  // After the resume upload completes, we walk through every newly-created
  // candidate and prompt the recruiter to fill in the missing profile fields.
  const [postUploadQueue, setPostUploadQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [isPostUploadFlow, setIsPostUploadFlow] = useState(false);

  // ----- core fetch state ---------------------------------------------------
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ----- edit-profile modal -------------------------------------------------
  const [editProfileModal, setEditProfileModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // ----- upload --------------------------------------------------------------
  const [showAddCandidatePopup, setShowAddCandidatePopup] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFilesStatus, setUploadFilesStatus] = useState([]);
  const [overallProgress, setOverallProgress] = useState(0);

  // ----- other modals -------------------------------------------------------
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [showResponsesViewer, setShowResponsesViewer] = useState(false);
  const [showJDModal, setShowJDModal] = useState(false);

  // ----- filter state -------------------------------------------------------
  const [activeFilter, setActiveFilter] = useState(initialFilter);

  // -------------------------------------------------------------------------
  // Toast helper — auto-dismiss after 5s.
  // -------------------------------------------------------------------------
  const showPopup = ({ type, title, message, closable = true }) => {
    setPopup({ open: true, type, title, message, closable });
    setTimeout(() => setPopup((p) => ({ ...p, open: false })), 5000);
  };

  const closeUploadPopupAndRefresh = () => {
    setShowAddCandidatePopup(false);
    setUploadFilesStatus([]);
    window.location.reload();
  };

  // -------------------------------------------------------------------------
  // Edit profile — same parsing logic as before, just lifted into the new UI
  // -------------------------------------------------------------------------
  const handleEditProfile = (candidate) => {
    setSelectedCandidate(candidate);

    const expYears = Math.floor(candidate.exp_yrs);
    const expMonths = Math.round((candidate.exp_yrs - expYears) * 12);
    const relExpYears = Math.floor(candidate.relevant_exp_yrs);
    const relExpMonths = Math.round(
      (candidate.relevant_exp_yrs - relExpYears) * 12,
    );

    // Notice period can be either an integer-as-string ("15") or an ISO date.
    // Detect which one and pre-populate the right radio + input.
    const rawNotice = candidate.notice_period;
    let noticeType = 'days';
    let noticeDays = '';
    let noticeDate = '';
    if (rawNotice == null || rawNotice === '') {
      // leave defaults
    } else if (isIntegerString(rawNotice)) {
      noticeType = 'days';
      noticeDays = String(Number(String(rawNotice).trim()));
    } else if (isIsoDateOrDatetime(rawNotice)) {
      noticeType = 'date';
      noticeDate = extractDatePart(rawNotice);
    }

    setEditFormData({
      name: candidate.name || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
      experienceYears: expYears,
      experienceMonths: expMonths,
      relExperienceYears: relExpYears,
      relExperienceMonths: relExpMonths,
      currentLocation: candidate.cur_loc || '',
      currentCTC: candidate.cur_ctc != null ? String(candidate.cur_ctc) : '',
      expectedCTC: candidate.exp_ctc != null ? String(candidate.exp_ctc) : '',
      noticeType,
      noticeDate,
      noticeDays,
      resume_path: candidate.resume_path,
      comment: candidate.comment,
    });
    setEditProfileModal(true);
  };

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      // ---- validate ------------------------------------------------------
      const errs = {};
      const phoneDigits = (editFormData.phone || '').replace(/\D/g, '');
      if (!editFormData.email) errs.email = 'Email is required';

      const years = Number(editFormData.experienceYears) || 0;
      const months = Number(editFormData.experienceMonths) || 0;
      if (isNaN(years) || years < 0) errs.experienceYears = 'Invalid years';
      if (isNaN(months) || months < 0 || months > 11)
        errs.experienceMonths = 'Months must be 0-11';

      const relYears = Number(editFormData.relExperienceYears) || 0;
      const relMonths = Number(editFormData.relExperienceMonths) || 0;
      if (isNaN(relYears) || relYears < 0)
        errs.relExperienceYears = 'Invalid years';
      if (isNaN(relMonths) || relMonths < 0 || relMonths > 11)
        errs.relExperienceMonths = 'Months must be 0-11';

      const parseDec = (v) => {
        if (v === '' || v == null) return null;
        const n = Number(v);
        return isNaN(n) ? NaN : n;
      };
      const curCTC = parseDec(editFormData.currentCTC);
      const expCTC = parseDec(editFormData.expectedCTC);
      if (editFormData.currentCTC && (isNaN(curCTC) || curCTC < 0))
        errs.currentCTC = 'Invalid current CTC';
      if (editFormData.expectedCTC && (isNaN(expCTC) || expCTC < 0))
        errs.expectedCTC = 'Invalid expected CTC';

      if (editFormData.noticeType === 'date' && !editFormData.noticeDate)
        errs.noticeDate = 'Pick a date';

      if (Object.keys(errs).length) {
        alert(Object.values(errs).join('\n'));
        return;
      }

      // ---- shape payload ------------------------------------------------
      const exp_yrs = Number(years) + (Number(months) || 0) / 12;
      const relevant_exp_yrs = Number(relYears) + (Number(relMonths) || 0) / 12;

      let notice_period_to_send = null;
      if (editFormData.noticeType === 'days') {
        notice_period_to_send =
          editFormData.noticeDays === ''
            ? ''
            : Number(editFormData.noticeDays || 0);
      } else if (editFormData.noticeType === 'date') {
        notice_period_to_send = editFormData.noticeDate || '';
      }

      const payload = {
        candidate_email: editFormData.email,
        name: editFormData.name || selectedCandidate.name,
        phone: phoneDigits || null,
        exp_yrs: Number(exp_yrs.toFixed(4)),
        relevant_exp_yrs: Number(relevant_exp_yrs.toFixed(4)),
        cur_ctc: curCTC != null ? Number(curCTC) : null,
        exp_ctc: expCTC != null ? Number(expCTC) : null,
        cur_loc:
          editFormData.currentLocation ||
          selectedCandidate.currentLocation ||
          selectedCandidate.cur_loc ||
          null,
        notice_period: notice_period_to_send,
        comment: editFormData.comment || '',
        id: selectedCandidate.id,
        resume_path: editFormData.resume_path,
      };

      if (!jobId) {
        alert(
          'Job id not found in selected candidate. Provide job id in selectedCandidate.job_id or set window.currentJobId.',
        );
        return;
      }

      const adminEmail =
        sessionStorage.getItem('admin_email') ||
        localStorage.getItem('admin_email');
      const url = getApiUrl(
        `/api/job_applications/${encodeURIComponent(jobId)}/update_candidate`,
      );

      const res = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Email': adminEmail || '',
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (!res.ok) {
        alert(result.error || result.message || 'Failed to save candidate');
        return;
      }

      // Optimistically merge changes into the local list so the UI updates
      // before the next page-level refresh.
      const updated = {
        ...selectedCandidate,
        name: payload.name,
        email: payload.candidate_email,
        phone: payload.phone,
        exp_yrs: payload.exp_yrs,
        relevant_exp_yrs: payload.relevant_exp_yrs,
        cur_ctc: payload.cur_ctc,
        exp_ctc: payload.exp_ctc,
        cur_loc: payload.cur_loc,
        notice_period: payload.notice_period,
        resume_path: payload.resume_path,
        comment: payload.comment,
      };
      setCandidates((prev) =>
        prev.map((c) =>
          c.email === selectedCandidate.email ? { ...c, ...updated } : c,
        ),
      );

      // Continue the post-upload walkthrough or close.
      if (isPostUploadFlow) {
        const nextIndex = queueIndex + 1;
        if (nextIndex < postUploadQueue.length) {
          setQueueIndex(nextIndex);
          handleEditProfile(postUploadQueue[nextIndex]);
        } else {
          setIsPostUploadFlow(false);
          setPostUploadQueue([]);
          setQueueIndex(0);
          setEditProfileModal(false);
          setSelectedCandidate(null);
          closeUploadPopupAndRefresh();
        }
      } else {
        setEditProfileModal(false);
        setSelectedCandidate(null);
        closeUploadPopupAndRefresh();
      }
    } catch (err) {
      console.error('Save profile failed', err);
      alert('Failed to save profile: ' + (err.message || err));
    } finally {
      setIsSavingProfile(false);
    }
  };

  // -------------------------------------------------------------------------
  // Upload — XHR-based for progress reporting (fetch can't expose upload %)
  // -------------------------------------------------------------------------
  const uploadSingleFile = (file, jobId, adminEmail, url, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const TIMEOUT_MS = 300000;

      const timeoutId = setTimeout(() => {
        xhr.abort();
        reject(
          new Error(
            'Upload timeout - file may be too large or connection is slow',
          ),
        );
      }, TIMEOUT_MS);

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
      });

      xhr.addEventListener('load', () => {
        clearTimeout(timeoutId);
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            reject(new Error('Invalid server response'));
          }
        } else {
          reject(
            new Error(xhr.responseText || `Upload failed with status ${xhr.status}`),
          );
        }
      });

      xhr.addEventListener('error', () => {
        clearTimeout(timeoutId);
        reject(new Error('Network error occurred during upload'));
      });

      xhr.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        reject(new Error('Upload was cancelled or timed out'));
      });

      const fd = new FormData();
      fd.append('file', file);
      fd.append('job_id', jobId);
      fd.append('recruiterEmail', recruiter.email || '');
      xhr.open('POST', url);
      xhr.withCredentials = true;
      xhr.setRequestHeader('X-Admin-Email', adminEmail || '');
      xhr.send(fd);
    });
  };

  const handleResumeUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    if (!jobId) {
      alert('Job ID is missing. Please try refreshing the page.');
      event.target.value = '';
      return;
    }

    // Reject obviously-wrong file types up front so we never POST them.
    const invalidFile = files.find((f) => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return !['pdf', 'doc', 'docx', 'txt'].includes(ext);
    });
    if (invalidFile) {
      alert(
        `Unsupported file format: ${invalidFile.name}. Please upload PDF/DOC/DOCX/TXT only.`,
      );
      event.target.value = '';
      return;
    }

    const initStatus = files.map((f) => ({
      name: f.name,
      progress: 0,
      status: 'queued',
      error: null,
    }));
    setUploadFilesStatus(initStatus);
    setIsUploading(true);
    setOverallProgress(0);

    const adminEmail = recruiter.email || '';
    const url = `${API_BASE_URL}/api/handle_resume_upload`;
    let successCount = 0;
    let duplicateCount = 0;
    let failedCount = 0;
    const allResults = [];
    const uploadedCandidates = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileIndex = i;

        setUploadFilesStatus((prev) =>
          prev.map((item, idx) =>
            idx === fileIndex ? { ...item, status: 'uploading', progress: 0 } : item,
          ),
        );
        const baseProgress = Math.floor((fileIndex / files.length) * 100);
        setOverallProgress(baseProgress);

        try {
          // 70% of the per-file slot is the upload, the remaining 30%
          // is the server-side parse phase reflected by the 'processing' state.
          const result = await uploadSingleFile(
            file,
            jobId,
            adminEmail,
            url,
            (progress) => {
              setUploadFilesStatus((prev) =>
                prev.map((item, idx) =>
                  idx === fileIndex
                    ? { ...item, progress: Math.round(progress * 0.7) }
                    : item,
                ),
              );
              const fileProgress = (progress * 0.7) / files.length;
              setOverallProgress(baseProgress + Math.round(fileProgress));
            },
          );

          setUploadFilesStatus((prev) =>
            prev.map((item, idx) =>
              idx === fileIndex
                ? { ...item, status: 'processing', progress: 80 }
                : item,
            ),
          );
          await new Promise((r) => setTimeout(r, 300));

          // Backend response shape (see app.py /api/handle_resume_upload):
          //   {
          //     "success":         true,
          //     "new_candidates":  [ {...candidate}, ... ],   ← snake_case + plural
          //     "duplicates":      [ {...}, ... ],
          //   }
          //
          // Bug fix: previous code checked `result.newCandidate` (camelCase +
          // singular) which the backend NEVER returns — every successful
          // upload was therefore flagged as "Processing failed" in the UI
          // even though the candidate row landed in the DB just fine. Now
          // we read the array the backend actually sends.
          const isDuplicate =
            Array.isArray(result?.duplicates) && result.duplicates.length > 0;
          const newCandidates = Array.isArray(result?.new_candidates)
            ? result.new_candidates
            : [];
          const isSuccess =
            result?.success === true && newCandidates.length > 0;

          if (isSuccess) {
            successCount++;
            // Push the first new candidate (one file = one candidate row).
            uploadedCandidates.push(newCandidates[0]);
            setUploadFilesStatus((prev) =>
              prev.map((item, idx) =>
                idx === fileIndex
                  ? { ...item, status: 'success', progress: 100, error: null }
                  : item,
              ),
            );
          } else if (isDuplicate) {
            duplicateCount++;
            setUploadFilesStatus((prev) =>
              prev.map((item, idx) =>
                idx === fileIndex
                  ? {
                      ...item,
                      status: 'duplicate',
                      progress: 100,
                      error: 'Already exists',
                    }
                  : item,
              ),
            );
          } else {
            failedCount++;
            setUploadFilesStatus((prev) =>
              prev.map((item, idx) =>
                idx === fileIndex
                  ? {
                      ...item,
                      status: 'failed',
                      progress: 0,
                      // Prefer the friendly server message (sent for credit /
                      // plan errors) over the raw error code.
                      error: result?.message || result?.error || 'Processing failed',
                    }
                  : item,
              ),
            );
          }
          allResults.push({
            file: file.name,
            result,
            success: isSuccess || isDuplicate,
          });
        } catch (err) {
          console.error(`Error uploading ${file.name}:`, err);
          failedCount++;
          const errorMessage =
            err.message.includes('timeout') || err.message.includes('time')
              ? 'Timeout - file too large or slow connection'
              : err.message || 'Upload failed';
          setUploadFilesStatus((prev) =>
            prev.map((item, idx) =>
              idx === fileIndex
                ? { ...item, status: 'failed', progress: 0, error: errorMessage }
                : item,
            ),
          );
          allResults.push({ file: file.name, error: err.message, success: false });
        }
        await new Promise((r) => setTimeout(r, 200));
      }

      setOverallProgress(100);
      await new Promise((r) => setTimeout(r, 500));

      if (successCount > 0) {
        window.dispatchEvent(new CustomEvent('refreshJobs'));
      }

      if (uploadedCandidates.length > 0) {
        setPostUploadQueue(uploadedCandidates);
        setQueueIndex(0);
        setIsPostUploadFlow(true);
        handleEditProfile(uploadedCandidates[0]);
      }

      if (successCount === files.length) {
        showPopup({
          type: 'success',
          title: 'Upload Successful',
          message: `All ${successCount} resume${
            successCount > 1 ? 's' : ''
          } uploaded and candidates enrolled successfully.`,
          closable: false,
        });
      } else if (successCount > 0) {
        const failedFiles = allResults.filter((r) => !r.success).map((r) => r.file);
        showPopup({
          type: 'warning',
          title: 'Partial Upload',
          message:
            `${successCount} successful\n` +
            `${duplicateCount} duplicate\n` +
            `${failedCount} failed: ${failedFiles.join(', ')}`,
        });
      } else if (duplicateCount === files.length) {
        showPopup({
          type: 'warning',
          title: 'Duplicate Upload',
          message: `All ${duplicateCount} file${
            duplicateCount > 1 ? 's are' : ' is'
          } already uploaded.`,
        });
      } else {
        showPopup({
          type: 'error',
          title: 'Upload Failed',
          message:
            'All files failed to upload. Please try again with fewer files or check your connection.',
        });
      }
    } catch (err) {
      console.error('Error in upload process:', err);
      alert(`Upload Error: ${err.message || 'An unexpected error occurred.'}`);
    } finally {
      setIsUploading(false);
      setTimeout(() => setOverallProgress(0), 2000);
      event.target.value = '';
    }
  };

  // -------------------------------------------------------------------------
  // Fetch job details
  // -------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const fetchJobDetails = async () => {
      if (!jobId || !recruiter?.email) {
        setError('Missing job ID or recruiter information');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({
          jobId,
          recruiterEmail: recruiter.email,
        });
        const res = await fetch(
          `${API_BASE_URL}/api/recruiter/job-details?${params}`,
          { credentials: 'include' },
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to fetch job details');
        }
        const data = await res.json();
        if (cancelled) return;
        setJob(data.job);
        setCandidates(data.candidates);
      } catch (e) {
        console.error('Error fetching job details:', e);
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchJobDetails();
    return () => {
      cancelled = true;
    };
  }, [jobId, recruiter?.email]);

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------
  // Filter predicate — one place that knows how to test a candidate
  // against a filter id. Kept as a small helper so the visible list
  // (`filteredCandidates`) and the count chip (`filterCount`) can't
  // drift apart.
  //
  // 'rejected' is special: rejection is tracked via the `isRejected`
  // boolean column (set by the company-side reject flow), NOT the
  // `status` string — `ja.status` is only written for shortlist /
  // hire / join transitions, so checking c.status === 'rejected' would
  // always miss.
  const matchesFilter = (c, id) => {
    if (id === 'all') return true;
    if (id === 'rejected') return Boolean(c?.isRejected);
    return (c?.status || '').toLowerCase() === id.toLowerCase();
  };

  const filteredCandidates = useMemo(() => {
    if (activeFilter === 'all') return candidates;
    return candidates.filter((c) => matchesFilter(c, activeFilter));
  }, [activeFilter, candidates]);

  const filterCount = (id) =>
    id === 'all'
      ? candidates.length
      : candidates.filter((c) => matchesFilter(c, id)).length;

  const composerCandidateList = filteredCandidates.map((c) => ({
    email:
      c.email ||
      `${(c.name || '').toLowerCase().replace(/\s+/g, '.')}@example.com`,
    name: c.name || '',
    designation: c.jobTitle || '',
  }));

  // -------------------------------------------------------------------------
  // Loading / error / not-found states
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <>
        <RecruiterNavbar />
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
            <Skeleton className="h-72 w-full rounded-lg" />
            <div className="flex flex-col gap-3">
              <Skeleton className="h-12 w-full rounded-lg" />
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <RecruiterNavbar />
        <div className="mx-auto flex w-full max-w-7xl items-center justify-center px-4 py-16">
          <Card className="w-full max-w-md border-destructive/30">
            <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h2 className="text-lg font-semibold">Error loading job details</h2>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (!job) {
    return (
      <>
        <RecruiterNavbar />
        <div className="mx-auto flex w-full max-w-7xl items-center justify-center px-4 py-16">
          <Card className="w-full max-w-md">
            <CardContent className="py-8 text-center">
              <h2 className="text-lg font-semibold">Job not found</h2>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <>
      <RecruiterNavbar />

      <div className="mx-auto w-full max-w-7xl px-4 py-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
          {/* ============================== Sidebar (filters) */}
          <aside>
            <Card className="border border-border/40 shadow-sm sticky top-20">
              <div className="border-b border-border/40 px-4 py-3">
                <h3 className="text-sm font-semibold">Filters</h3>
              </div>
              <CardContent className="space-y-1 p-2">
                {FILTERS.map(({ id, label, icon: Icon }) => {
                  const active = activeFilter === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setActiveFilter(id)}
                      className={cn(
                        'group flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                        active
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                      )}
                      aria-pressed={active}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="flex-1 text-left">{label}</span>
                      <Badge
                        variant="outline"
                        className={cn(
                          'h-5 border-0 px-1.5 text-[10px] font-medium',
                          active ? 'bg-primary/20 text-primary' : 'bg-muted',
                        )}
                      >
                        {filterCount(id)}
                      </Badge>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </aside>

          {/* ============================== Main content */}
          <main className="flex flex-col gap-4">
            {/* Header */}
            <Card className="border border-border/40 shadow-sm">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div>
                    <h1 className="text-xl font-bold tracking-tight">
                      {job.title}
                    </h1>
                    <p className="text-xs text-muted-foreground">
                      {job.location} · {job.experience}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowJDModal(true)}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    View JD
                  </Button>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant="outline"
                    className="gap-1.5 border-primary/40 bg-primary/5 px-3 py-1 text-primary"
                  >
                    <Users className="h-3.5 w-3.5" />
                    <span className="font-semibold">
                      {filteredCandidates.length}
                    </span>
                    <span className="text-xs">candidates</span>
                  </Badge>

                  <Button
                    size="sm"
                    onClick={() => setShowAddCandidatePopup(true)}
                  >
                    <Plus className="mr-1.5 h-4 w-4" />
                    Add Candidate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowEmailComposer(true)}
                  >
                    <Mail className="mr-1.5 h-4 w-4" />
                    Send Questionnaire
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowResponsesViewer(true)}
                  >
                    <ClipboardList className="mr-1.5 h-4 w-4" />
                    View Responses
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Candidate list */}
            <div className="flex flex-col gap-3">
              {filteredCandidates.length === 0 ? (
                <Card className="border border-border/40 shadow-sm">
                  <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
                    <Inbox className="h-10 w-10 text-muted-foreground/50" />
                    <h3 className="text-base font-semibold">
                      No candidates found
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      There are no candidates matching the selected filter.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                filteredCandidates.map((candidate) => (
                  <CandidateCardTabbed
                    key={candidate.id}
                    candidate={candidate}
                    jobId={jobId}
                    job={job}
                    recruiterEmail={recruiter.email}
                    onEditProfile={handleEditProfile}
                  />
                ))
              )}
            </div>
          </main>
        </div>
      </div>

      {/* ============================== Toast */}
      <Toast
        open={popup.open}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        closable={popup.closable}
        onClose={() => setPopup((p) => ({ ...p, open: false }))}
      />

      {/* ============================== Edit profile modal */}
      <Dialog
        open={editProfileModal && !!selectedCandidate}
        onOpenChange={(o) => {
          if (!o) {
            setEditProfileModal(false);
            setSelectedCandidate(null);
          }
        }}
      >
        <DialogContent className="max-w-6xl p-0">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Resume preview */}
            <div className="flex h-[640px] flex-col border-r border-border/40 bg-muted/30">
              <div className="flex items-center justify-between border-b border-border/40 px-4 py-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold">Resume Preview</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const url =
                      editFormData.resume_path || selectedCandidate?.resume_path;
                    if (!url) return alert('No resume available');
                    // Through the authenticated backend, never the raw S3 link.
                    downloadResume(url, selectedCandidate?.name);
                  }}
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  Download
                </Button>
              </div>
              <div className="flex-1 overflow-hidden">
                {editFormData.resume_path || selectedCandidate?.resume_path ? (
                  // PDF → iframe; .docx → rendered in-page; .doc → download card
                  <ResumePreview url={editFormData.resume_path || selectedCandidate.resume_path} />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    No resume available
                  </div>
                )}
              </div>
            </div>

            {/* Edit form */}
            <div className="flex h-[640px] flex-col overflow-y-auto">
              <DialogHeader className="border-b border-border/40 px-6 py-4">
                <DialogTitle>Edit Profile</DialogTitle>
              </DialogHeader>

              <div className="flex-1 space-y-4 px-6 py-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Name">
                    <Input
                      value={editFormData.name || ''}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, name: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Email">
                    <Input
                      type="email"
                      value={editFormData.email || ''}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, email: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Phone">
                    <Input
                      type="tel"
                      inputMode="numeric"
                      placeholder="10 digits"
                      value={editFormData.phone || ''}
                      onChange={(e) => {
                        const digits = e.target.value
                          .replace(/\D/g, '')
                          .slice(0, 10);
                        setEditFormData({ ...editFormData, phone: digits });
                      }}
                    />
                  </Field>
                  <Field label="Current Location">
                    <Input
                      value={editFormData.currentLocation || ''}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          currentLocation: e.target.value,
                        })
                      }
                    />
                  </Field>
                </div>

                <Field label="Total Experience">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="50"
                      placeholder="Years"
                      value={editFormData.experienceYears ?? ''}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          experienceYears:
                            e.target.value === ''
                              ? ''
                              : Math.max(0, Math.floor(Number(e.target.value))),
                        })
                      }
                    />
                    <Input
                      type="number"
                      min="0"
                      max="11"
                      placeholder="Months"
                      value={editFormData.experienceMonths ?? 0}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          experienceMonths:
                            e.target.value === ''
                              ? ''
                              : Math.max(
                                  0,
                                  Math.min(
                                    11,
                                    Math.floor(Number(e.target.value)),
                                  ),
                                ),
                        })
                      }
                    />
                  </div>
                </Field>

                <Field label="Relevant Experience">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="50"
                      placeholder="Years"
                      value={editFormData.relExperienceYears ?? ''}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          relExperienceYears:
                            e.target.value === ''
                              ? ''
                              : Math.max(0, Math.floor(Number(e.target.value))),
                        })
                      }
                    />
                    <Input
                      type="number"
                      min="0"
                      max="11"
                      placeholder="Months"
                      value={editFormData.relExperienceMonths ?? 0}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          relExperienceMonths:
                            e.target.value === ''
                              ? ''
                              : Math.max(
                                  0,
                                  Math.min(
                                    11,
                                    Math.floor(Number(e.target.value)),
                                  ),
                                ),
                        })
                      }
                    />
                  </div>
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Current CTC (LPA)">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g., 6.50"
                      value={editFormData.currentCTC || ''}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          currentCTC: e.target.value,
                        })
                      }
                    />
                  </Field>
                  <Field label="Expected CTC (LPA)">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g., 8.00"
                      value={editFormData.expectedCTC || ''}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          expectedCTC: e.target.value,
                        })
                      }
                    />
                  </Field>
                </div>

                <Field label="Resume Path / URL">
                  <div className="flex items-center gap-2">
                    <Input
                      type="url"
                      value={
                        editFormData.resume_path ||
                        selectedCandidate?.resume_path ||
                        ''
                      }
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          resume_path: e.target.value,
                        })
                      }
                      placeholder="https://s3... or /resumes/abc.pdf"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      title="Download resume"
                      onClick={() => {
                        const url =
                          editFormData.resume_path ||
                          selectedCandidate?.resume_path;
                        if (!url) return alert('No resume path provided');
                        downloadResume(url, selectedCandidate?.name);
                      }}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </Field>

                <Field label="Notice Period">
                  <RadioGroup
                    value={editFormData.noticeType || 'days'}
                    onValueChange={(v) =>
                      setEditFormData({
                        ...editFormData,
                        noticeType: v,
                        ...(v === 'days' ? { noticeDate: '' } : { noticeDays: '' }),
                      })
                    }
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="date" id="notice-date" />
                      <Label htmlFor="notice-date" className="text-sm font-normal">
                        Till specific date
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="days" id="notice-days" />
                      <Label htmlFor="notice-days" className="text-sm font-normal">
                        In number of days
                      </Label>
                    </div>
                  </RadioGroup>

                  {editFormData.noticeType === 'date' ? (
                    <Input
                      type="date"
                      className="mt-2"
                      value={editFormData.noticeDate || ''}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          noticeDate: e.target.value,
                        })
                      }
                    />
                  ) : (
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="365"
                        placeholder="e.g., 15"
                        value={editFormData.noticeDays || ''}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            noticeDays: e.target.value,
                          })
                        }
                      />
                      <span className="text-sm text-muted-foreground">days</span>
                    </div>
                  )}
                </Field>

                <Field label="Comment">
                  <Textarea
                    rows={4}
                    value={editFormData.comment || ''}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        comment: e.target.value,
                      })
                    }
                  />
                </Field>
              </div>

              <DialogFooter className="border-t border-border/40 px-6 py-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (isPostUploadFlow) {
                      const nextIndex = queueIndex + 1;
                      if (nextIndex < postUploadQueue.length) {
                        setQueueIndex(nextIndex);
                        handleEditProfile(postUploadQueue[nextIndex]);
                      } else {
                        setIsPostUploadFlow(false);
                        setPostUploadQueue([]);
                        setQueueIndex(0);
                        setEditProfileModal(false);
                        setSelectedCandidate(null);
                      }
                    } else {
                      setEditProfileModal(false);
                      setSelectedCandidate(null);
                    }
                  }}
                >
                  Skip for now
                </Button>
                <Button onClick={handleSaveProfile} disabled={isSavingProfile}>
                  {isSavingProfile ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============================== Add Candidate (upload) modal */}
      <Dialog
        open={showAddCandidatePopup}
        onOpenChange={(o) => {
          if (!o && !isUploading) closeUploadPopupAndRefresh();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Candidate Resume
              {uploadFilesStatus.length > 1 ? 's' : ''}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {!isUploading && uploadFilesStatus.length === 0 && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Upload candidate resume(s) to automatically parse and enroll them
                  for this job.
                </p>
                <Badge variant="outline">Job ID: {jobId}</Badge>
              </div>
            )}

            {isUploading && uploadFilesStatus.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium truncate">
                    {(() => {
                      const cur = uploadFilesStatus.find(
                        (f) => f.status === 'uploading' || f.status === 'processing',
                      );
                      const completed = uploadFilesStatus.filter((f) =>
                        ['success', 'duplicate', 'failed'].includes(f.status),
                      ).length;
                      if (cur)
                        return cur.status === 'uploading'
                          ? `Uploading: ${cur.name}`
                          : `Processing: ${cur.name}`;
                      if (completed === uploadFilesStatus.length)
                        return 'All files processed';
                      return 'Preparing files…';
                    })()}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {overallProgress}%
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${overallProgress}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {(() => {
                    const completed = uploadFilesStatus.filter((f) =>
                      ['success', 'duplicate', 'failed'].includes(f.status),
                    ).length;
                    return `${completed} of ${uploadFilesStatus.length} processed`;
                  })()}
                </p>
              </div>
            )}

            {!isUploading && (
              <>
                <input
                  type="file"
                  id="jd-resume-upload"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleResumeUpload}
                  className="hidden"
                  multiple
                />
                <label
                  htmlFor="jd-resume-upload"
                  className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border/60 bg-muted/30 px-6 py-10 text-center transition-colors hover:bg-muted/50"
                >
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    Click to select resume(s)
                  </span>
                  <span className="text-xs text-muted-foreground">
                    or drag and drop here
                  </span>
                </label>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline">PDF, DOC, DOCX, TXT</Badge>
                  <Badge variant="outline">Multiple files allowed</Badge>
                </div>
              </>
            )}

            {uploadFilesStatus.length > 0 && (
              <div className="max-h-64 space-y-2 overflow-y-auto">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {isUploading ? 'Upload Progress' : 'Upload Results'}
                </h4>
                {uploadFilesStatus.map((f, idx) => (
                  <UploadFileRow
                    key={idx}
                    status={f.status}
                    name={f.name}
                    progress={f.progress}
                    error={f.error}
                  />
                ))}
              </div>
            )}
          </div>

          {!isUploading && uploadFilesStatus.length > 0 && (
            <DialogFooter>
              <Button variant="outline" onClick={closeUploadPopupAndRefresh}>
                Close
              </Button>
              <Button
                onClick={() => {
                  setUploadFilesStatus([]);
                  document.getElementById('jd-resume-upload')?.click();
                }}
              >
                Upload More
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* ============================== Other modals (rendered as before) */}
      {showEmailComposer && (
        <EmailComposer
          candidates={composerCandidateList}
          jobIdToUse={job.job_id}
          onClose={() => setShowEmailComposer(false)}
        />
      )}

      {showResponsesViewer && (
        <QuestionnaireResponsesViewer
          jobId={job.job_id}
          onClose={() => setShowResponsesViewer(false)}
        />
      )}

      {showJDModal && job && (
        <JobDescriptionModal
          jobId={jobId}
          recruiterEmail={recruiter?.email}
          onClose={() => setShowJDModal(false)}
        />
      )}
    </>
  );
}

// Tiny composable form-field wrapper used inside the edit-profile dialog.
// Extracting it keeps the long form readable at the call sites.
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

export default JobDetails;
